import type { Workflow } from '@/client/services/api/graphql/gql/graphql';
import { create } from 'zustand';

export interface Step {
  label: string;
  value: string;
  formComponent: JSX.Element;
  previewComponent: JSX.Element;
  isDisabled: boolean;
}

type PreviewStatus = 'notStarted' | 'inProgress' | 'completed';

type FormValue =
  | 'generalDetails'
  | 'automations'
  | 'audience'
  | 'content'
  | 'settings'
  | 'notifications';

type PreviewStatusMap = {
  [key in string]: PreviewStatus;
};

export const defaultPreviewStatusMap: PreviewStatusMap = {
  generalDetails: 'notStarted',
  automations: 'notStarted',
  audience: 'notStarted',
  content: 'notStarted',
  settings: 'notStarted',
  notifications: 'notStarted',
};

interface AdminAssignmentsState {
  steps: Step[];
  setSteps: (steps: Step[]) => void;
  stepIndex: number;
  setStepIndex: (stepIndex: number) => void;
  activeForm: string;
  setActiveForm: (activeForm: string) => void;
  selectedInstanceCreationData: Workflow | null;
  setSelectedInstanceCreationData: (selectedInstanceData: Workflow | null) => void;
  selectedWorkflowDuplicateData: Workflow | null;
  setSelectedWorkflowDuplicateData: (selectedWorkflowDuplicateData: Workflow | null) => void;
  previewStatusMap: PreviewStatusMap;
  setPreviewStatusMap: (previewStatusMap: PreviewStatusMap) => void;
  settingsFormVisited: boolean;
  setSettingsFormVisited: (settingsFormVisited: boolean) => void;
  notificationsFormVisited: boolean;
  setNotificationsFormVisited: (notificationsFormVisited: boolean) => void;
  audienceFormVisited: boolean;
  setAudienceFormVisited: (audienceFormVisited: boolean) => void;
  currentInstanceParentWorkflowId: string | null;
  setCurrentInstanceParentWorkflowId: (currentInstanceParentWorkflowId: string | null) => void;
  audienceCount: number;
  setAudienceCount: (audienceCount: number) => void;
  audience500ModalIsOpen: boolean;
  setAudience500ModalIsOpen: (audience500ModalIsOpen: boolean) => void;
  audienceExceedsLimitModalIsOpen: boolean;
  setAudienceExceedsLimitModalIsOpen: (audienceExceedsLimitModalIsOpen: boolean) => void;
  canLeave: boolean;
  setCanLeave: (canLeave: boolean) => void;
}

export const useAdminAssignmentsStore = create<AdminAssignmentsState>((set) => ({
  steps: [],
  setSteps: (steps) => set({ steps }),
  stepIndex: 0,
  setStepIndex: (stepIndex) => set({ stepIndex }),
  activeForm: '',
  setActiveForm: (activeForm) => set({ activeForm }),
  selectedInstanceCreationData: null,
  setSelectedInstanceCreationData: (selectedInstanceCreationData) =>
    set({ selectedInstanceCreationData }),
  selectedWorkflowDuplicateData: null,
  setSelectedWorkflowDuplicateData: (selectedWorkflowDuplicateData) =>
    set({ selectedWorkflowDuplicateData }),
  previewStatusMap: {
    generalDetails: 'notStarted',
    automations: 'notStarted',
    audience: 'notStarted',
    content: 'notStarted',
    settings: 'notStarted',
    notifications: 'notStarted',
  },
  setPreviewStatusMap: (previewStatusMap) => set({ previewStatusMap }),
  settingsFormVisited: false,
  setSettingsFormVisited: (settingsFormVisited) => set({ settingsFormVisited }),
  notificationsFormVisited: false,
  setNotificationsFormVisited: (notificationsFormVisited) => set({ notificationsFormVisited }),
  audienceFormVisited: false,
  setAudienceFormVisited: (audienceFormVisited) => set({ audienceFormVisited }),
  currentInstanceParentWorkflowId: null,
  setCurrentInstanceParentWorkflowId: (currentInstanceParentWorkflowId) =>
    set({ currentInstanceParentWorkflowId }),
  audienceCount: 0,
  setAudienceCount: (audienceCount) => set({ audienceCount }),
  audience500ModalIsOpen: false,
  setAudience500ModalIsOpen: (audience500ModalIsOpen) => set({ audience500ModalIsOpen }),
  audienceExceedsLimitModalIsOpen: false,
  setAudienceExceedsLimitModalIsOpen: (audienceExceedsLimitModalIsOpen) =>
    set({ audienceExceedsLimitModalIsOpen }),
  canLeave: false,
  setCanLeave: (canLeave) => set({ canLeave }),
}));
